import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { IJiraAttachment } from "../../common/interface";
import { useAttachmentContext } from "../../context/AttachmentContext";
import { getJiraAttachment } from "../../services/jira.service";
import FilePreview from "../FilePreview";
import "./style.scss";

export interface IADFMark {
  type: string;
  attrs?: Record<string, any>;
}

export interface IADFContentItem {
  type: string;
  text?: string;
  content?: IADFContentItem[];
  marks?: IADFMark[];
  attrs?: Record<string, any>;
}

export interface IADFTableCell extends IADFContentItem {
  type: "tableCell" | "tableHeader";
  attrs?: {
    colspan?: number;
    rowspan?: number;
    background?: string;
  };
  content: IADFContentItem[];
}

export interface IADFTableRow extends IADFContentItem {
  type: "tableRow";
  content: IADFTableCell[];
}

export interface IADFTable extends IADFContentItem {
  type: "table";
  attrs?: {
    layout?: "default" | "wide" | "full-width";
    isClickable?: boolean;
  };
  content: IADFTableRow[];
}

interface ADFTextProps {
  item: IADFContentItem;
}

const applyMarks = (text: string, marks: IADFMark[] = []): JSX.Element => {
  let element: JSX.Element = <>{text}</>;
  marks.forEach((mark) => {
    switch (mark.type) {
      case "strong":
        element = <strong className="strong-marks">{element}</strong>;
        break;
      case "em":
        element = <em>{element}</em>;
        break;
      case "link":
        if (mark.attrs?.href) {
          element = (
            <Link
              href={mark.attrs.href}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              {element}
            </Link>
          );
        }
        break;
      case "code":
        element = <code className="code-snippet">{element}</code>;
        break;
      case "strike":
        element = <s>{element}</s>;
        break;
      case "underline":
        element = <u>{element}</u>;
        break;
      case "subsup":
        if (mark.attrs?.type === "sub") {
          element = <sub>{element}</sub>;
        } else if (mark.attrs?.type === "sup") {
          element = <sup>{element}</sup>;
        }
        break;
      default:
        console.warn(`Unknown mark type: ${mark.type}`);
        break;
    }
  });

  return element;
};

export const ADFText: React.FC<ADFTextProps> = ({ item }) => {
  if (!item.text) return null;
  return (
    <Typography
      component="span"
      sx={{
        display: "flex",
      }}
    >
      {applyMarks(item.text, item.marks)}
    </Typography>
  );
};

interface ADFParagraphProps {
  item: IADFContentItem;
  index: number;
}

export const ADFParagraph: React.FC<ADFParagraphProps> = ({ item, index }) => (
  <Typography
    key={`paragraph_${index}`}
    component="p"
    sx={{ my: 1, display: "flex" }}
  >
    <ADFRenderer content={item.content ?? []} />
  </Typography>
);

interface ADFListProps {
  item: IADFContentItem;
  index: number;
}

export const ADFBulletList: React.FC<ADFListProps> = ({ item, index }) => (
  <ul key={`bulletList` + index} className="list-disc">
    {(item.content ?? []).map((subItem, subIndex) => (
      <li key={`listItem_${index}_${subIndex}`} className="">
        <ADFRenderer content={[subItem]} />
      </li>
    ))}
  </ul>
);

interface ADFMentionProps {
  item: IADFContentItem;
  index: number;
}

export const ADFMention: React.FC<ADFMentionProps> = ({ item, index }) => (
  <Link
    key={`${item.attrs?.id}_${index}`}
    href={`#user-${item.attrs?.id}`}
    target="_blank"
    underline="hover"
    className="mention-container"
    sx={{ pointerEvents: "none", color: "info.main", display: "flex" }}
  >
    {item.attrs?.text ?? "Unknown User"}
  </Link>
);

export const ADFOrderedList: React.FC<ADFListProps> = ({ item, index }) => (
  <ol
    key={`orderedList_${index}`}
    className="list-decimal"
    start={item.attrs?.order ?? 1}
  >
    {(item.content ?? []).map((subItem, subIndex) => (
      <li key={`listItem_${index}_${subIndex}`} className="">
        <ADFRenderer content={[subItem]} />
      </li>
    ))}
  </ol>
);

export const ADFListItem: React.FC<ADFListProps> = ({ item }) => (
  <ADFRenderer content={item.content ?? []} />
);

interface ADFHardBreakProps {
  index: number;
}

export const ADFHardBreak: React.FC<ADFHardBreakProps> = ({ index }) => (
  <br key={`hardBreak_${index}`} />
);

interface CodeBlockWithCopyProps {
  item: IADFContentItem;
  index: number;
}

export const ADFCodeBlock: React.FC<CodeBlockWithCopyProps> = ({
  item,
  index,
}) => {
  const [copied, setCopied] = React.useState(false);

  const codeContent = item.content?.map((node) => node.text).join("\n") || "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <Box
      key={"CodeBlock" + index}
      sx={{
        position: "relative",
        backgroundColor: "#f5f5f5",
        padding: 2,
        borderRadius: "5px",
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        overflowX: "auto",
        my: 2,
      }}
    >
      <Tooltip title="Copy to clipboard">
        <IconButton
          onClick={() => handleCopy()}
          sx={{
            position: "absolute",
            top: 1,
            right: 1,
            color: "text.secondary",
            "&:hover": {
              color: "text.primary",
            },
          }}
          aria-label="copy code"
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Typography
        component="pre"
        sx={{ margin: 0, display: "flex", textAlign: "start" }}
      >
        {codeContent}
      </Typography>

      <Snackbar
        open={copied}
        message="Code copied to clipboard!"
        autoHideDuration={1500}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

interface ADFMediaProps {
  item: IADFContentItem;
  index: number;
}

export const ADFMediaSingle: React.FC<ADFMediaProps> = ({ item, index }) => (
  <Box key={`mediaSingle_${index}`} sx={{ my: 2 }}>
    {item.content && item.content.length > 0 && (
      <ADFRenderer content={item.content} />
    )}
    {!item?.content?.length && item?.attrs && (
      <ADFMedia item={item} index={index} />
    )}
  </Box>
);

const getHrefFromDataId = (
  html: string,
  mediaServiceId: string
): string | null => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const linkElement = doc.querySelector(
    `*[data-media-services-id="${mediaServiceId}"]`
  );

  return linkElement ? linkElement.getAttribute("href") : null;
};

export const ADFMedia: React.FC<ADFMediaProps> = ({ item, index }) => {
  const { attachments, renderedFields, setViewAttachmentIndex } =
    useAttachmentContext();

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [mediaInfo, setMediaInfo] = useState<IJiraAttachment | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  let content: string | undefined;

  const foundAttachmentIndex = attachments?.findIndex(
    (attachment) =>
      attachment?.mimeType?.includes("image") &&
      attachment?.filename === item.attrs?.alt
  );

  if (foundAttachmentIndex >= 0) {
    const foundAttachment = attachments[foundAttachmentIndex];
    content = foundAttachment?.content;
  }

  const getFileData = useCallback(async () => {
    setMediaInfo(null);
    if (item.attrs?.id && renderedFields?.description) {
      const mediaContentPath = getHrefFromDataId(
        renderedFields.description,
        item.attrs?.id
      );
      if (mediaContentPath) {
        const mediaData = attachments?.find((attachment) =>
          attachment?.content?.includes(mediaContentPath)
        );
        if (mediaData?.content) {
          setMediaInfo(mediaData);
        }
      }
    }
  }, [item, attachments, renderedFields]);

  const getJiraMedia = async (srcUrl: string) => {
    setImageSrc(null);
    try {
      setLoading(true);
      const imgdata = await getJiraAttachment(srcUrl);
      const imgUrl = URL.createObjectURL(imgdata);
      setImageSrc(imgUrl);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching Jira attachment:", error);
    }
  };

  useEffect(() => {
    if (content) {
      getJiraMedia(content);
    } else {
      getFileData();
    }
  }, [content, item, getFileData]);

  const onOpenAttachmentPreview = (fileInfo?: IJiraAttachment) => {
    if (foundAttachmentIndex >= 0) {
      setViewAttachmentIndex(foundAttachmentIndex);
    } else if (fileInfo?.content) {
      const mediaIndex = attachments?.findIndex(
        (attachment) => attachment?.content === fileInfo?.content
      );
      setViewAttachmentIndex(mediaIndex >= 0 ? mediaIndex : null);
    }
  };

  return (
    <Typography
      key={`media_${index}`}
      component="span"
      className="media-container"
      sx={{ display: "flex", pb: 2, mb: 1 }}
      onClick={() => onOpenAttachmentPreview()}
    >
      {mediaInfo && (
        <FilePreview
          file={mediaInfo}
          onClick={() => {
            onOpenAttachmentPreview(mediaInfo);
          }}
        />
      )}
      {!mediaInfo && !imageSrc && !loading && (
        <>
          <span className="media-text">{item.attrs?.type ?? "File"} : </span>
          <span className="file-name">
            {item.attrs?.alt ?? item.attrs?.id ?? "No file name available"}
          </span>
        </>
      )}

      {loading && (
        <Typography variant="body2" color="textSecondary">
          Loading...
        </Typography>
      )}

      {imageSrc && !loading && (
        <img
          src={imageSrc}
          alt={item?.attrs?.alt || "Media attachment"}
          style={{
            maxWidth: "100%",
            height: "auto",
            display: "block",
            marginTop: 8,
          }}
        />
      )}
    </Typography>
  );
};

interface ADFTableProps {
  item: IADFTable;
  index: number;
}
export const ADFTableComponent: React.FC<ADFTableProps> = ({ item, index }) => {
  if (!item.content || item.content.length === 0) {
    return null;
  }
  const renderRow = (row: IADFTableRow, rowIndex: number) => {
    const isHeaderRow = row.content.some((cell) => cell.type === "tableHeader");
    const CellComponent = TableCell;

    return (
      <TableRow
        key={`tableRow_${index}_${rowIndex}`}
        sx={{ backgroundColor: rowIndex % 2 === 0 ? "#f9f9f9" : "white" }}
      >
        {row.content.map((cell: IADFTableCell, cellIndex: number) => (
          <CellComponent
            key={`tableCell_${index}_${rowIndex}_${cellIndex}`}
            colSpan={cell.attrs?.colspan}
            rowSpan={cell.attrs?.rowspan}
            sx={{
              backgroundColor: cell.attrs?.background || "inherit",
              border: "1px solid #e0e0e0",
              padding: "8px 16px",
              verticalAlign: "middle",
              fontWeight: isHeaderRow ? "bold" : "normal",
              textAlign: "left",
            }}
          >
            <ADFRenderer content={cell.content ?? []} />
          </CellComponent>
        ))}
      </TableRow>
    );
  };

  // Separate header and body rows
  const headerRows = item.content.filter((row) =>
    row.content.some((cell) => cell.type === "tableHeader")
  );
  const bodyRows = item.content.filter(
    (row) => !row.content.some((cell) => cell.type === "tableHeader")
  );

  return (
    <TableContainer component={Paper} sx={{ my: 2, overflowX: "auto" }}>
      <Table stickyHeader aria-label="ADF Table">
        {headerRows.length > 0 && (
          <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
            {headerRows.map((row, rowIndex) => renderRow(row, rowIndex))}
          </TableHead>
        )}
        <TableBody>
          {bodyRows.map((row, rowIndex) =>
            renderRow(row, rowIndex + headerRows.length)
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface ADFUnknownProps {
  item: IADFContentItem;
  index: number;
}

export const ADFUnknown: React.FC<ADFUnknownProps> = ({ item, index }) => {
  console.warn(`Unknown ADF node type encountered: ${item.type}`, item);

  return (
    <Box
      key={`unknown_${item.type}_${index}`}
      sx={{ my: 1, display: "flex", p: 1 }}
    >
      {item?.text}
    </Box>
  );
};

const InlineCardRenderer: React.FC<{
  item: IADFContentItem;
  index: number;
}> = ({ item, index }) => {
  const { attrs } = item;

  if (!attrs?.url) {
    return null;
  }

  return (
    <Link
      key={`InlineCard` + index}
      href={attrs.url}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
    >
      <Typography
        variant="body2"
        color="primary"
        sx={{
          display: "flex",
        }}
      >
        {attrs.url}
      </Typography>
    </Link>
  );
};

type ADFNodeRenderer = {
  [key: string]: React.FC<{ item: IADFContentItem; index: number }>;
};

const nodeRenderers: ADFNodeRenderer = {
  text: ADFText,
  paragraph: ADFParagraph,
  bulletList: ADFBulletList,
  orderedList: ADFOrderedList,
  listItem: ADFListItem,
  mention: ADFMention,
  hardBreak: ADFHardBreak,
  codeBlock: ADFCodeBlock,
  mediaSingle: ADFMediaSingle,
  mediaGroup: ADFMediaSingle,
  mediaInline: ADFMediaSingle,
  media: ADFMedia,
  table: ADFTableComponent as unknown as React.FC<{
    item: IADFContentItem;
    index: number;
  }>,
  inlineCard: InlineCardRenderer,
};

export interface ADFRendererProps {
  content: IADFContentItem[];
}

export const ADFRenderer: React.FC<ADFRendererProps> = ({ content }) => {
  if (!content || content.length === 0) {
    return null;
  }

  return (
    <div className="ADF-renderer-container">
      {content.map((item, index) => {
        const Component = nodeRenderers[item.type];
        if (Component) {
          return (
            <Component
              key={`${item.type}_${index}`}
              item={item}
              index={index}
            />
          );
        } else {
          return (
            <ADFUnknown
              key={`unknown_${item.type}_${index}`}
              item={item}
              index={index}
            />
          );
        }
      })}
    </div>
  );
};
